import React, { useCallback, useEffect, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import { motion } from 'framer-motion';
import {
  CreditCard, Home, Mail, Map, MessageSquare, Network,
  Newspaper, Phone, Search, Settings, Trash2, TrendingUp, Users, Wrench
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import ktcLogo from '../../assets/ktc.png';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import StaffPageHeader from '../../components/StaffPageHeader';

const API = `${resolveBackendBaseUrl()}/api`;

const AdminContacts = () => {
  const [user, setUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const toText = (value) => String(value ?? '').trim();
  const toPhoneDigits = (value) => toText(value).replace(/[^0-9]/g, '');


  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setContacts(data);
    } catch (error) {
      toast.error('Gagal memuat pesan');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const storedUser = localStorage.getItem('ktc_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchContacts();
  }, [fetchContacts]);

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, { headers: getAuthHeaders() });
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, is_read: true } : c)));
      if (selectedContact?.id === id) {
        setSelectedContact((prev) => (prev ? { ...prev, is_read: true } : prev));
      }
    } catch (error) {
      toast.error('Gagal menandai pesan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus pesan ini?')) return;
    
    try {
      await axios.delete(`${API}/contact/${id}`, { headers: getAuthHeaders() });
      toast.success('Pesan berhasil dihapus!');
      setContacts((prev) => prev.filter((c) => c.id !== id));
      setSelectedContact((prev) => (prev?.id === id ? null : prev));
    } catch (error) {
      toast.error('Gagal menghapus pesan');
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    if (!contact.is_read) {
      handleMarkRead(contact.id);
    }
  };

  const filteredContacts = contacts.filter((item) => {
    const query = toText(searchQuery).toLowerCase();
    const matchesSearch =
      toText(item?.name).toLowerCase().includes(query) ||
      toText(item?.email).toLowerCase().includes(query) ||
      toText(item?.message).toLowerCase().includes(query) ||
      toText(item?.phone).toLowerCase().includes(query);

    if (filter === 'unread') return matchesSearch && !item.is_read;
    if (filter === 'read') return matchesSearch && item.is_read;
    return matchesSearch;
  });

  const unreadCount = contacts.filter(c => !c.is_read).length;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedContactName = toText(selectedContact?.name) || 'Tanpa Nama';
  const selectedContactEmail = toText(selectedContact?.email);
  const selectedContactPhone = toText(selectedContact?.phone);
  const selectedContactPhoneDigits = toPhoneDigits(selectedContact?.phone);
  const selectedContactMessage = toText(selectedContact?.message) || '-';

  return (
    <>
      {loading && (
        <StaffPageLoadingOverlay text="Memuat data pesan..." />
      )}

      {/* Main Content */}
      <AppShell>
      <PageContainer>
        {/* Header */}
        <StaffPageHeader
          className="mb-8"
          eyebrow="Komunikasi"
          title="Pesan Masuk"
          subtitle="Kelola pesan dari pengunjung website secara cepat."
        />

        {/* Filter & Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Cari pesan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#121212] border-white/10 text-white"
              data-testid="search-contacts-input"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-brand-blue text-white' : 'btn-secondary'}
            >
              Semua
            </Button>
            <Button
              variant={filter === 'unread' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('unread')}
              className={filter === 'unread' ? 'bg-brand-red text-white' : 'btn-secondary'}
            >
              Belum Dibaca ({unreadCount})
            </Button>
            <Button
              variant={filter === 'read' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('read')}
              className={filter === 'read' ? 'bg-gray-600 text-white' : 'btn-secondary'}
            >
              Sudah Dibaca
            </Button>
          </div>
        </div>

        {/* Contacts List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-brand-blue border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-20 bg-[#121212] rounded-xl border border-white/10">
            <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Tidak ada pesan</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredContacts.map((contact, index) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleViewContact(contact)}
                className={`
                  bg-[#121212] rounded-xl p-4 border cursor-pointer transition-all hover:border-brand-blue/30
                  ${contact.is_read ? 'border-white/10' : 'border-brand-blue/30 bg-brand-blue/5'}
                `}
                data-testid={`contact-item-${contact.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className={`font-medium ${contact.is_read ? 'text-white' : 'text-brand-blue'}`}>
                        {toText(contact.name) || 'Tanpa Nama'}
                      </h3>
                      {!contact.is_read && (
                        <span className="px-2 py-0.5 bg-brand-blue text-white text-[10px] rounded-full font-medium">
                          Baru
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-2">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {toText(contact.email) || '-'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {toText(contact.phone) || '-'}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm line-clamp-2">{toText(contact.message) || '-'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-gray-500 text-xs">{formatDate(contact.created_at)}</p>
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleDelete(contact.id); }}
                        className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
                  </PageContainer>
    </AppShell>

      {/* View Contact Modal */}
      <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Detail Pesan</DialogTitle>
          </DialogHeader>

          {selectedContact && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider">Dari</label>
                <p className="text-white font-medium">{selectedContactName}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Email</label>
                  {selectedContactEmail ? (
                    <a href={`mailto:${selectedContactEmail}`} className="text-brand-blue hover:underline block">
                      {selectedContactEmail}
                    </a>
                  ) : (
                    <span className="text-gray-400 block">-</span>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Telepon</label>
                  {selectedContactPhone ? (
                    <a href={`tel:${selectedContactPhone}`} className="text-brand-blue hover:underline block">
                      {selectedContactPhone}
                    </a>
                  ) : (
                    <span className="text-gray-400 block">-</span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider">Tanggal</label>
                <p className="text-gray-300">{formatDate(selectedContact.created_at)}</p>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider">Pesan</label>
                <p className="text-gray-300 whitespace-pre-wrap bg-[#1a1a1a] p-4 rounded-lg mt-1">
                  {selectedContactMessage}
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-white/10">
                {selectedContactEmail ? (
                  <a 
                    href={`mailto:${selectedContactEmail}`}
                    className="flex-1"
                  >
                    <Button className="btn-primary w-full text-white">
                      <Mail className="w-4 h-4 mr-2" />
                      Balas Email
                    </Button>
                  </a>
                ) : (
                  <Button disabled className="flex-1 btn-primary w-full text-white opacity-50">
                    <Mail className="w-4 h-4 mr-2" />
                    Balas Email
                  </Button>
                )}
                {selectedContactPhoneDigits ? (
                  <a 
                    href={`https://wa.me/${selectedContactPhoneDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full btn-secondary">
                      <Phone className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                ) : (
                  <Button disabled variant="outline" className="flex-1 w-full btn-secondary opacity-50">
                    <Phone className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminContacts;














